import { I18n } from '@calendly/i18n'
import { IconAlignJustified } from '@calendly/ui/components/glyphs/IconAlignJustified'
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown'
import { IconCircles } from '@calendly/ui/components/glyphs/IconCircles'
import { IconForms } from '@calendly/ui/components/glyphs/IconForms'
import { IconPhone } from '@calendly/ui/components/glyphs/IconPhone'
import { uniqBy } from 'lodash-es'
import { nanoid } from 'nanoid'

import { hasAnswerChoices } from 'components/event_type_edit/invitee_questions'

export const NAME_EDITING_KEY = 'name'
export const MAX_CUSTOM_QUESTIONS = 10

const BOOKING_PAGE = 'publisher.event_type_editor_crud.booking_page'

// Icons match Event-type-editor v4 Figma question-type menu (node 2737:227701).
const ANSWER_TYPE_CONFIG = {
  string: {
    labelKey: `${BOOKING_PAGE}.short_answer`,
    descriptionKey: `${BOOKING_PAGE}.short_answer_description`,
    icon: IconForms,
  },
  text: {
    labelKey: `${BOOKING_PAGE}.long_answer`,
    descriptionKey: `${BOOKING_PAGE}.long_answer_description`,
    icon: IconAlignJustified,
  },
  choices_one: {
    labelKey: `${BOOKING_PAGE}.multiple_choice`,
    descriptionKey: `${BOOKING_PAGE}.multiple_choice_description`,
    icon: IconCircles,
  },
  choices_many: {
    labelKey: `${BOOKING_PAGE}.multiple_choice`,
    descriptionKey: `${BOOKING_PAGE}.multiple_choice_description`,
    icon: IconCircles,
  },
  select: {
    labelKey: `${BOOKING_PAGE}.dropdown_selection`,
    descriptionKey: `${BOOKING_PAGE}.dropdown_selection_description`,
    icon: IconChevronDown,
  },
  phone_number: {
    labelKey: `${BOOKING_PAGE}.phone_number`,
    descriptionKey: `${BOOKING_PAGE}.phone_number_description`,
    icon: IconPhone,
  },
}

const getAnswerTypeConfig = (format) => ANSWER_TYPE_CONFIG[format] ?? null

export const getQuestionTypeIcon = (format) => getAnswerTypeConfig(format)?.icon ?? IconForms

export const getAnswerTypeBadgeLabel = (format) => {
  const config = getAnswerTypeConfig(format)
  return config ? I18n.t(config.labelKey) : null
}

export const getAnswerTypeMenuLabel = (format) => getAnswerTypeBadgeLabel(format)

export const getAnswerTypeMenuItems = () => (
  ['string', 'text', 'choices_one', 'select', 'phone_number'].map((format) => {
    const config = getAnswerTypeConfig(format)

    return {
      value: format,
      label: I18n.t(config.labelKey),
      description: I18n.t(config.descriptionKey),
    }
  })
)

export const reorderQuestions = (questions, items) => {
  const findNewIdx = (question) => items.findIndex((item) => item.id === question.id)
  const newOrder = items.slice().sort((a, b) => findNewIdx(a) - findNewIdx(b))
  const newOrderForShownItems = newOrder.map((question, idx) => ({
    ...question,
    position: idx,
  }))

  return uniqBy([...newOrderForShownItems, ...questions], 'id')
}

export const generateBlankQuestion = (position, format = 'string') => ({
  id: `new${nanoid(5)}`,
  name: '',
  format,
  required: false,
  enabled: true,
  answerChoices: null,
  include_other: false,
  isCreationFlow: true,
  position,
})

export const snapshotQuestionValues = (question) => ({
  name: question.name,
  format: question.format,
  required: question.required,
  enabled: question.enabled,
  answerChoices: question.answerChoices,
  include_other: question.include_other,
})

export const getEditingQuestionFieldNames = (index, format) => {
  const fieldNames = [`inviteeQuestions.${index}.name`]

  if (hasAnswerChoices(format)) {
    fieldNames.push(`inviteeQuestions.${index}.answerChoices`)
  }

  return fieldNames
}

export const snapshotNameFormatValue = (nameFormat) => nameFormat

export const isMultipleChoiceFormat = (format) => (
  format === 'choices_one' || format === 'choices_many'
)

const getFilledChoicePreviewItems = (question) => {
  const choiceItems = (question.answerChoices || [])
    .map(({ id, choice }) => ({
      id,
      label: choice?.trim(),
    }))
    .filter(({ id, label }) => id && label)

  if (question.include_other) {
    choiceItems.push({
      id: `${question.id}-other`,
      label: I18n.t('publisher.booking_modal.other'),
    })
  }

  return choiceItems
}

/**
 * Collapsed-card choice preview.
 * Multiple choice → list each option. Dropdown → "{count} choices" (Figma).
 */
export const getCollapsedChoicePreview = (question) => {
  if (!question || !hasAnswerChoices(question.format)) {
    return { mode: 'none', items: [], count: 0 }
  }

  const items = getFilledChoicePreviewItems(question)
  if (items.length === 0) {
    return { mode: 'none', items: [], count: 0 }
  }

  if (question.format === 'select') {
    return { mode: 'count', items: [], count: items.length }
  }

  return { mode: 'list', items, count: items.length }
}
